import {
  ClauseCategory,
  ClauseValidationOptionType,
  ParticipantStatus,
  SignatureFieldType,
  SignatureType,
  SigningFileType,
  SigningProvider,
  SigningRequestSigneeRole,
  SigningRequestStatus,
  ContractManagementColumnExtractionScope,
  ContractManagementContractFileType,
  ContractTypeTemplateKey,
  type ContractTypeRef,
  IntakeAgentLogStatus,
  PlaybookActionType,
  PlaybookEvaluationType,
  PlaybookExtractionResponseType,
  PlaybookPartyPosition,
  PlaybookRiskLevel,
  PlaybookRunStatus,
  PlaybookStatus,
  PreprocessingTaskStatus,
  ReviewTaskStatus,
  TaskStatus,
  VaultFileIntakeStatus,
  VaultFileRevisionSource,
} from '@/api';
// The option sets are owned and exported by the components; these maps only
// adapt backend enums into them.
import type { GaiaIndicatorStatus } from './base/GaiaStatusIndicator.vue';
import type { GaiaRiskLevel } from './base/GaiaRiskPill.vue';
import type { GaiaClauseTier } from './playbook/GaiaClauseTierChip.vue';
import type { GaiaChipTone } from './base/GaiaStatusChip.vue';
import type { GaiaGridCellState } from './domain/grid/GaiaGridCell.vue';

/** How one backend status value renders: indicator + glyph + human label. */
export interface GaiaStatusDescriptor {
  status: GaiaIndicatorStatus;
  glyph?: 'dot' | 'check';
  label: string;
}

/*
 * Canonical mappings from the GENERATED api enums to the design system's
 * indicator. Each map is an exhaustive Record over its enum, so when the
 * backend adds a member (and `make api_client` regenerates the types),
 * vue-tsc fails here until the new member is mapped — views must consume
 * these maps instead of hand-rolling their own status → color logic.
 *
 * The base TaskStatus semantics (shared string values across the specialized
 * enums): pending dot → running pulse → green check when done; failed red;
 * partial/warning amber; skipped/cancelled neutral.
 */

export const taskStatusMap: Record<TaskStatus, GaiaStatusDescriptor> = {
  [TaskStatus.PENDING]: { status: 'pending', label: 'Pending' },
  [TaskStatus.RUNNING]: { status: 'running', label: 'Running' },
  [TaskStatus.DONE]: { status: 'success', glyph: 'check', label: 'Done' },
  [TaskStatus.FAILED]: { status: 'alert', label: 'Failed' },
  [TaskStatus.PARTIAL]: { status: 'warning', label: 'Partially completed' },
  [TaskStatus.SKIPPED]: { status: 'neutral', label: 'Skipped' },
  [TaskStatus.CANCELLED]: { status: 'neutral', label: 'Cancelled' },
  [TaskStatus.WARNING]: { status: 'warning', label: 'Finished with warnings' },
};

export const preprocessingTaskStatusMap: Record<PreprocessingTaskStatus, GaiaStatusDescriptor> = {
  [PreprocessingTaskStatus.PENDING]: { status: 'pending', label: 'Pending' },
  [PreprocessingTaskStatus.RUNNING]: { status: 'running', label: 'Running' },
  [PreprocessingTaskStatus.DONE]: { status: 'success', glyph: 'check', label: 'Done' },
  [PreprocessingTaskStatus.FAILED]: { status: 'alert', label: 'Failed' },
  [PreprocessingTaskStatus.PARTIAL]: { status: 'warning', label: 'Partially completed' },
  [PreprocessingTaskStatus.SKIPPED]: { status: 'neutral', label: 'Skipped' },
  [PreprocessingTaskStatus.CANCELLED]: { status: 'neutral', label: 'Cancelled' },
  [PreprocessingTaskStatus.WARNING]: { status: 'warning', label: 'Finished with warnings' },
  [PreprocessingTaskStatus.ARCHIVE]: { status: 'neutral', label: 'Archived' },
};

export const reviewTaskStatusMap: Record<ReviewTaskStatus, GaiaStatusDescriptor> = {
  [ReviewTaskStatus.PENDING]: { status: 'pending', label: 'Pending' },
  [ReviewTaskStatus.RUNNING]: { status: 'running', label: 'Running' },
  [ReviewTaskStatus.DONE]: { status: 'success', glyph: 'check', label: 'Done' },
  [ReviewTaskStatus.FAILED]: { status: 'alert', label: 'Failed' },
  [ReviewTaskStatus.PARTIAL]: { status: 'warning', label: 'Partially completed' },
  [ReviewTaskStatus.SKIPPED]: { status: 'neutral', label: 'Skipped' },
  [ReviewTaskStatus.CANCELLED]: { status: 'neutral', label: 'Cancelled' },
  [ReviewTaskStatus.WARNING]: { status: 'warning', label: 'Finished with warnings' },
  [ReviewTaskStatus.OCR_IN_PROGRESS]: { status: 'running', label: 'OCR in progress' },
  [ReviewTaskStatus.EXTRACTION_IN_PROGRESS]: { status: 'running', label: 'Extraction in progress' },
  [ReviewTaskStatus.MERGE_IN_PROGRESS]: { status: 'running', label: 'Merging results' },
  [ReviewTaskStatus.EVALUATION_IN_PROGRESS]: { status: 'running', label: 'Evaluation in progress' },
  [ReviewTaskStatus.SUGGESTION_IN_PROGRESS]: { status: 'running', label: 'Drafting suggestions' },
};

/**
 * ReviewTaskStatus | TaskStatus → indicator descriptor.
 *
 * The two enums overlap by value — TaskStatus is ReviewTaskStatus minus the
 * five `*_IN_PROGRESS` pipeline stages — but TypeScript keeps them distinct,
 * and a review's `processing_state` arrives as whichever the endpoint returned.
 * Built by merging both exhaustive maps rather than casting between them, so
 * vue-tsc still breaks if either enum gains a member.
 */
export const anyTaskStatusMap: Record<ReviewTaskStatus | TaskStatus, GaiaStatusDescriptor> = {
  ...taskStatusMap,
  ...reviewTaskStatusMap,
};

export const playbookRunStatusMap: Record<PlaybookRunStatus, GaiaStatusDescriptor> = {
  [PlaybookRunStatus.PENDING]: { status: 'pending', label: 'Pending' },
  [PlaybookRunStatus.RUNNING]: { status: 'running', label: 'Running' },
  [PlaybookRunStatus.DONE]: { status: 'success', glyph: 'check', label: 'Done' },
  [PlaybookRunStatus.FAILED]: { status: 'alert', label: 'Failed' },
  [PlaybookRunStatus.PARTIAL]: { status: 'warning', label: 'Partially completed' },
  [PlaybookRunStatus.SKIPPED]: { status: 'neutral', label: 'Skipped' },
  [PlaybookRunStatus.CANCELLED]: { status: 'neutral', label: 'Cancelled' },
  [PlaybookRunStatus.WARNING]: { status: 'warning', label: 'Finished with warnings' },
  [PlaybookRunStatus.SUMMARIZING]: { status: 'running', label: 'Summarizing' },
};

/**
 * VaultFileIntakeStatus → where an uploaded file stands on its way into a
 * contract (History list and detail). The three in-flight stages
 * pulse; `archive` is the container a zip was unpacked from, not a failure.
 */
export const vaultFileIntakeStatusMap: Record<VaultFileIntakeStatus, GaiaStatusDescriptor> = {
  [VaultFileIntakeStatus.PREPROCESSING]: { status: 'running', label: 'Reading' },
  [VaultFileIntakeStatus.CONVERTING]: { status: 'running', label: 'Sorting' },
  [VaultFileIntakeStatus.EXTRACTING]: { status: 'running', label: 'Extracting parties' },
  [VaultFileIntakeStatus.IN_CONTRACT]: { status: 'success', label: 'In contract' },
  [VaultFileIntakeStatus.ARCHIVE]: { status: 'neutral', label: 'Archive' },
  [VaultFileIntakeStatus.FAILED]: { status: 'alert', label: 'Failed' },
  [VaultFileIntakeStatus.NOT_CONVERTED]: { status: 'alert', label: 'Not converted' },
};

/** VaultFileRevisionSource → the entry point a file came in through (History's Source filter). */
export const vaultFileRevisionSourceMap: Record<VaultFileRevisionSource, string> = {
  [VaultFileRevisionSource.USER_UPLOAD]: 'Upload',
  [VaultFileRevisionSource.DATA_ROOM_UPLOAD]: 'Data room upload',
  [VaultFileRevisionSource.INTAKE_EMAIL]: 'Email intake',
  [VaultFileRevisionSource.INTAKE_SLACK]: 'Slack intake',
  [VaultFileRevisionSource.INTAKE_JIRA]: 'Jira intake',
  [VaultFileRevisionSource.INTAKE_AGENT_API]: 'Intake agent API',
  [VaultFileRevisionSource.GOOGLE_DRIVE_IMPORT]: 'Google Drive import',
  [VaultFileRevisionSource.GAIA_IMPORT]: 'GAIA import',
  [VaultFileRevisionSource.CONTRACT_DRAFT]: 'Contract draft',
  [VaultFileRevisionSource.TEMPLATE_AUTHORING]: 'Template authoring',
  [VaultFileRevisionSource.SIGNED_DOCUMENT]: 'Signed document',
  [VaultFileRevisionSource.SIGNING_CLEARED]: 'Signing cleared',
  [VaultFileRevisionSource.PDF_CONVERSION]: 'PDF conversion',
  [VaultFileRevisionSource.PLAYBOOK_COMMENTS]: 'Playbook comments',
  [VaultFileRevisionSource.DEMO]: 'Demo',
};

export const intakeAgentLogStatusMap: Record<IntakeAgentLogStatus, GaiaStatusDescriptor> = {
  [IntakeAgentLogStatus.PENDING]: { status: 'pending', label: 'Pending' },
  [IntakeAgentLogStatus.RUNNING]: { status: 'running', label: 'Running' },
  [IntakeAgentLogStatus.DONE]: { status: 'success', glyph: 'check', label: 'Done' },
  [IntakeAgentLogStatus.FAILED]: { status: 'alert', label: 'Failed' },
  [IntakeAgentLogStatus.PARTIAL]: { status: 'warning', label: 'Partially completed' },
  [IntakeAgentLogStatus.SKIPPED]: { status: 'neutral', label: 'Skipped' },
  [IntakeAgentLogStatus.CANCELLED]: { status: 'neutral', label: 'Cancelled' },
  [IntakeAgentLogStatus.WARNING]: { status: 'warning', label: 'Finished with warnings' },
  [IntakeAgentLogStatus.RECEIVED]: { status: 'pending', label: 'Received' },
  [IntakeAgentLogStatus.SCHEDULED]: { status: 'pending', label: 'Scheduled' },
  [IntakeAgentLogStatus.NOT_RECIPIENT]: { status: 'neutral', label: 'Not a recipient' },
};

/** PlaybookRiskLevel → GaiaRiskPill level + label. */
export const playbookRiskLevelMap: Record<
  PlaybookRiskLevel,
  { level: GaiaRiskLevel; label: string }
> = {
  [PlaybookRiskLevel.NONE]: { level: 'none', label: 'No risk' },
  [PlaybookRiskLevel.LOW]: { level: 'low', label: 'Low' },
  [PlaybookRiskLevel.MEDIUM]: { level: 'medium', label: 'Medium' },
  [PlaybookRiskLevel.HIGH]: { level: 'high', label: 'High' },
};

/**
 * Review evaluation answer → GaiaRiskPill level + label.
 *
 * Unlike every other map here this one is NOT keyed on a generated enum: a
 * review's evaluation answer is free-form text produced by whichever playbook
 * ran, and an org can configure its own answers. These four are the product's
 * canonical vocabulary — the same convention `evaluationOptions` in
 * `domain/extraction/GaiaExtractionFieldRow.vue` already encodes — and anything
 * else falls through to `'none'`, rendering as a plain chip carrying the
 * answer's own text. Precedent, not a contract; do not add a `Record<>` type
 * here expecting vue-tsc to police it.
 */
export const reviewEvaluationRiskMap: Record<
  string,
  { level: GaiaRiskLevel; label: string } | undefined
> = {
  High: { level: 'high', label: 'High' },
  Medium: { level: 'medium', label: 'Medium' },
  'Human Review': { level: 'medium', label: 'Human Review' },
  Low: { level: 'low', label: 'Low' },
};

/** The risk level for one evaluation answer, `'none'` when it isn't one of ours. */
export function reviewRiskLevel(answer: string | undefined): GaiaRiskLevel {
  if (!answer) return 'none';
  return reviewEvaluationRiskMap[answer]?.level ?? 'none';
}

/**
 * Playbook vocabularies (Playbooks mock: list, create wizard, runs).
 *
 * The mock also draws a "Needs review" playbook status; the backend has no
 * such member, so it is deliberately absent here — the list shows the three
 * real states only.
 */
export const playbookStatusMap: Record<PlaybookStatus, GaiaStatusDescriptor> = {
  [PlaybookStatus.DRAFT]: { status: 'neutral', label: 'Draft' },
  [PlaybookStatus.ACTIVE]: { status: 'success', label: 'Active' },
  [PlaybookStatus.ARCHIVED]: { status: 'pending', label: 'Archived' },
};

/** ClauseValidationOptionType → GaiaClauseTierChip tier + label. */
export const clauseTierMap: Record<
  ClauseValidationOptionType,
  { tier: GaiaClauseTier; label: string }
> = {
  [ClauseValidationOptionType.PREFERRED]: { tier: 'preferred', label: 'Preferred position' },
  [ClauseValidationOptionType.FALLBACK]: { tier: 'fallback', label: 'Acceptable fallback' },
  [ClauseValidationOptionType.ESCALATION]: {
    tier: 'escalation',
    label: 'Escalation required when',
  },
};

/** PlaybookEvaluationType → the wizard step it belongs to, and its chip label. */
export const playbookEvaluationTypeMap: Record<
  PlaybookEvaluationType,
  { step: 'extractions' | 'evaluations' | 'uncommons' | 'clauses'; label: string }
> = {
  [PlaybookEvaluationType.EXTRACTION]: { step: 'extractions', label: 'Extraction' },
  [PlaybookEvaluationType.CLASSIFICATION]: { step: 'evaluations', label: 'Evaluation' },
  [PlaybookEvaluationType.UNCOMMONS_CHECK]: { step: 'uncommons', label: 'Uncommon' },
  [PlaybookEvaluationType.CLAUSE_VALIDATION]: { step: 'clauses', label: 'Clause' },
};

/** PlaybookExtractionResponseType → the response-type chip / select label. */
export const extractionResponseTypeMap: Record<PlaybookExtractionResponseType, string> = {
  [PlaybookExtractionResponseType.STR]: 'Text',
  [PlaybookExtractionResponseType.INT]: 'Whole number',
  [PlaybookExtractionResponseType.FLOAT]: 'Decimal number',
  [PlaybookExtractionResponseType.BOOL]: 'Yes/No',
  [PlaybookExtractionResponseType.DATE]: 'Date',
  [PlaybookExtractionResponseType.DATETIME]: 'Date & time',
  [PlaybookExtractionResponseType.LIST_STR]: 'List of text',
  [PlaybookExtractionResponseType.LIST_INT]: 'List of whole numbers',
  [PlaybookExtractionResponseType.LIST_FLOAT]: 'List of decimal numbers',
  [PlaybookExtractionResponseType.LIST_BOOL]: 'List of Yes/No',
  [PlaybookExtractionResponseType.LIST_DATE]: 'List of dates',
  [PlaybookExtractionResponseType.LIST_DATETIME]: 'List of dates & times',
};

/** PlaybookPartyPosition → the list column / setup select label. */
export const playbookPartyPositionMap: Record<PlaybookPartyPosition, string> = {
  [PlaybookPartyPosition.COMPANY]: 'Company',
  [PlaybookPartyPosition.THIRD_PARTY]: 'Third party',
  [PlaybookPartyPosition.NEUTRAL]: 'Neutral',
};

/** How one backend value renders as a GaiaStatusChip. */
export interface GaiaChipDescriptor {
  tone: GaiaChipTone;
  label: string;
}

/**
 * Signing vocabularies (Document Signing list, Signature Request Detail,
 * Prepare-signature wizard).
 *
 * The mocks draw a couple of states the backend doesn't have — "Awaiting
 * approval" and "Declined" on the list, "Not yet notified" on a participant.
 * The maps below follow the generated enums; a view that wants the mock's
 * finer split has to derive it from the participants, not invent a status.
 */
/**
 * A variant's async template validation (contract templating). PENDING and
 * RUNNING both read as "Validating" — the split is a worker detail the user
 * can't act on differently.
 */
export const templateValidationChipMap: Record<TaskStatus, GaiaChipDescriptor> = {
  [TaskStatus.PENDING]: { tone: 'warning', label: 'Validating' },
  [TaskStatus.RUNNING]: { tone: 'warning', label: 'Validating' },
  [TaskStatus.DONE]: { tone: 'success', label: 'Ready' },
  [TaskStatus.FAILED]: { tone: 'alert', label: 'Failed' },
  [TaskStatus.PARTIAL]: { tone: 'warning', label: 'Partially validated' },
  [TaskStatus.SKIPPED]: { tone: 'neutral', label: 'Skipped' },
  [TaskStatus.CANCELLED]: { tone: 'neutral', label: 'Cancelled' },
  [TaskStatus.WARNING]: { tone: 'warning', label: 'Validated with warnings' },
};

export const signingRequestStatusMap: Record<SigningRequestStatus, GaiaChipDescriptor> = {
  [SigningRequestStatus.DRAFT]: { tone: 'neutral', label: 'Draft' },
  [SigningRequestStatus.PENDING]: { tone: 'accent', label: 'Pending' },
  [SigningRequestStatus.WAITING_FOR_SIGNATURES]: {
    tone: 'warning',
    label: 'Awaiting signature',
  },
  [SigningRequestStatus.SIGNED]: { tone: 'success', label: 'Completed' },
  [SigningRequestStatus.REJECTED]: { tone: 'alert', label: 'Declined' },
  [SigningRequestStatus.EXPIRED]: { tone: 'neutral', label: 'Expired' },
  [SigningRequestStatus.ERROR]: { tone: 'alert', label: 'Error' },
  [SigningRequestStatus.CANCELLED]: { tone: 'neutral', label: 'Cancelled' },
};

/**
 * ParticipantStatus → chip + whether a reminder still makes sense. Reminding
 * someone who already signed, declined or errored is noise, so `canRemind` is
 * part of the mapping rather than a guess at the call site.
 */
export const participantStatusMap: Record<
  ParticipantStatus,
  GaiaChipDescriptor & { canRemind: boolean }
> = {
  // Not notified yet (e.g. waiting their turn in an ordered flow) — nothing to re-send.
  [ParticipantStatus.INITIATED]: { tone: 'neutral', label: 'Waiting', canRemind: false },
  [ParticipantStatus.NOTIFIED]: { tone: 'warning', label: 'Notified', canRemind: true },
  [ParticipantStatus.VIEWED]: { tone: 'warning', label: 'Viewed', canRemind: true },
  [ParticipantStatus.SIGNED]: { tone: 'success', label: 'Signed', canRemind: false },
  [ParticipantStatus.APPROVED]: { tone: 'success', label: 'Approved', canRemind: false },
  [ParticipantStatus.DECLINED]: { tone: 'alert', label: 'Declined', canRemind: false },
  [ParticipantStatus.REJECTED]: { tone: 'alert', label: 'Rejected', canRemind: false },
  [ParticipantStatus.ABORTED]: { tone: 'alert', label: 'Aborted', canRemind: false },
  [ParticipantStatus.ERROR]: { tone: 'alert', label: 'Error', canRemind: false },
  [ParticipantStatus.DELIVERY_FAILED]: {
    tone: 'alert',
    label: 'Delivery failed',
    canRemind: true,
  },
  [ParticipantStatus.IDENTIFICATION_BLOCKED]: {
    tone: 'alert',
    label: 'Identification blocked',
    canRemind: false,
  },
  [ParticipantStatus.IDENTIFICATION_FAILED]: {
    tone: 'alert',
    label: 'Identification failed',
    canRemind: true,
  },
  [ParticipantStatus.IDENTIFICATION_SUCCEEDED]: {
    tone: 'accent',
    label: 'Identified',
    canRemind: false,
  },
  [ParticipantStatus.IDENTITY_SAVED]: { tone: 'accent', label: 'Identity saved', canRemind: false },
  // Mid-signature (Youtrust `processing` / `consent_given`) — nudging now is noise.
  [ParticipantStatus.PROCESSING]: { tone: 'accent', label: 'Signing', canRemind: false },
  [ParticipantStatus.SENDER_CONTACTED]: {
    tone: 'accent',
    label: 'Sender contacted',
    canRemind: false,
  },
};

/**
 * Participant states that count toward "x/y signed". Kept separate from `canRemind`
 * because "no reminder makes sense" also covers declined, aborted and not-yet-notified.
 */
export const signedParticipantStates: ReadonlySet<ParticipantStatus> = new Set([
  ParticipantStatus.SIGNED,
  ParticipantStatus.APPROVED,
]);

/** SigningRequestSigneeRole → the recipient card's role heading. */
export const signeeRoleMap: Record<SigningRequestSigneeRole, string> = {
  [SigningRequestSigneeRole.SIGNER]: 'Signer',
  [SigningRequestSigneeRole.APPROVER]: 'Approver',
  [SigningRequestSigneeRole.FOLLOWER]: 'Follower',
};

/** SignatureType → the per-recipient signature-level select label. */
export const signatureTypeMap: Record<SignatureType, string> = {
  [SignatureType.ELECTRONIC_SIGNATURE]: 'Simple Electronic Signature (SES)',
  [SignatureType.ADVANCED_ELECTRONIC_SIGNATURE]: 'Advanced Electronic Signature (AES)',
  [SignatureType.QUALIFIED_ELECTRONIC_SIGNATURE]: 'Qualified Electronic Signature (QES)',
};

/** SignatureFieldType → the field palette / placed-field label. */
export const signatureFieldTypeMap: Record<SignatureFieldType, string> = {
  [SignatureFieldType.SIGNATURE]: 'Signature',
  [SignatureFieldType.CHECKBOX]: 'Checkbox',
  [SignatureFieldType.MENTION]: 'Mention',
  [SignatureFieldType.TEXT]: 'Text',
  [SignatureFieldType.SIGNATURE_DATE]: 'Date',
  [SignatureFieldType.SIGNER_NAME]: 'Name',
  [SignatureFieldType.SIGNER_EMAIL]: 'Email',
  [SignatureFieldType.READ_ONLY_TEXT]: 'Read-only text',
};

/** SigningFileType → the nature select on a selected document. */
export const signingFileTypeMap: Record<SigningFileType, string> = {
  [SigningFileType.SIGNABLE_DOCUMENT]: 'Signable document',
  [SigningFileType.ATTACHMENT]: 'Attachment',
};

/** SigningProvider → the provider select label in step 1. */
export const signingProviderMap: Record<SigningProvider, string> = {
  [SigningProvider.GAIA_YOUTRUST]: 'GAIA (Youtrust)',
  [SigningProvider.YOUTRUST_INTEGRATION]: 'Youtrust (own account)',
};

/*
 * Contract-management view vocabularies (CM Views / View Create / View Detail
 * mocks): the spreadsheet's cell states, the re-extraction scope a column
 * change applies to, and the file types nested under a contract row.
 */

/**
 * TaskStatus → how a grid cell draws while its extraction runs. `DONE` lands on
 * `value`, and the caller decides between `value` and `blank` by whether the
 * cell actually has one — the status alone can't tell.
 */
export const contractCellStateMap: Record<TaskStatus, { state: GaiaGridCellState; label: string }> =
  {
    [TaskStatus.PENDING]: { state: 'pending', label: 'Pending extraction' },
    [TaskStatus.RUNNING]: { state: 'running', label: 'Extracting…' },
    [TaskStatus.DONE]: { state: 'value', label: '' },
    [TaskStatus.FAILED]: { state: 'failed', label: 'Extraction failed' },
    [TaskStatus.PARTIAL]: { state: 'value', label: '' },
    [TaskStatus.WARNING]: { state: 'value', label: '' },
    [TaskStatus.SKIPPED]: { state: 'blank', label: 'Skipped' },
    [TaskStatus.CANCELLED]: { state: 'blank', label: 'Cancelled' },
  };

/**
 * ReviewTaskStatus → how one insight-grid cell draws.
 *
 * Deliberately not `contractCellStateMap`: that one is keyed on `TaskStatus`
 * (8 members) and reusing it here would silently collapse the five
 * `*_IN_PROGRESS` pipeline stages a review extraction reports.
 */
export const reviewCellStateMap: Record<
  ReviewTaskStatus,
  { state: GaiaGridCellState; label: string }
> = {
  [ReviewTaskStatus.PENDING]: { state: 'pending', label: 'Pending extraction' },
  [ReviewTaskStatus.RUNNING]: { state: 'running', label: 'Running…' },
  [ReviewTaskStatus.DONE]: { state: 'value', label: '' },
  [ReviewTaskStatus.FAILED]: { state: 'failed', label: 'Extraction failed' },
  [ReviewTaskStatus.PARTIAL]: { state: 'value', label: '' },
  [ReviewTaskStatus.WARNING]: { state: 'value', label: '' },
  [ReviewTaskStatus.SKIPPED]: { state: 'blank', label: 'Skipped' },
  [ReviewTaskStatus.CANCELLED]: { state: 'blank', label: 'Cancelled' },
  [ReviewTaskStatus.OCR_IN_PROGRESS]: { state: 'running', label: 'OCR in progress…' },
  [ReviewTaskStatus.EXTRACTION_IN_PROGRESS]: { state: 'running', label: 'Extracting…' },
  [ReviewTaskStatus.MERGE_IN_PROGRESS]: { state: 'running', label: 'Merging results…' },
  [ReviewTaskStatus.EVALUATION_IN_PROGRESS]: { state: 'running', label: 'Evaluating…' },
  [ReviewTaskStatus.SUGGESTION_IN_PROGRESS]: { state: 'running', label: 'Drafting suggestions…' },
};

/** ContractManagementColumnExtractionScope → the drawer's "apply to" options. */
export const columnExtractionScopeMap: Record<ContractManagementColumnExtractionScope, string> = {
  [ContractManagementColumnExtractionScope.NEW_ONLY]: 'New contracts only',
  [ContractManagementColumnExtractionScope.LAST_5]: 'Last 5 contracts',
  [ContractManagementColumnExtractionScope.LAST_10]: 'Last 10 contracts',
  [ContractManagementColumnExtractionScope.ALL]: 'All contracts in the view',
};

/** ContractManagementContractFileType → the label on an expanded child row. */
export const contractFileTypeMap: Record<ContractManagementContractFileType, string> = {
  [ContractManagementContractFileType.CONTRACT]: 'Contract',
  [ContractManagementContractFileType.AMENDMENT]: 'Amendment',
  [ContractManagementContractFileType.APPENDIX]: 'Appendix',
};

/** A contract file's type label; null until the AI has classified the file. */
export function contractFileTypeLabel(type: ContractManagementContractFileType | null): string {
  return type ? contractFileTypeMap[type] : 'Unclassified';
}

/**
 * ClauseCategory → the label on a clause-library row, filter pill and category
 * select.
 *
 * The backend values are already prose, so these labels are deliberately the
 * enum values themselves. The map earns its place anyway: it fixes the order
 * the category select offers (alphabetical, `Other` last, which is what the
 * form has always shown) and it makes a new backend category a vue-tsc error
 * rather than an unlabelled option.
 */
export const clauseCategoryMap: Record<ClauseCategory, string> = {
  [ClauseCategory.AMENDMENT]: 'Amendment',
  [ClauseCategory.ASSIGNMENT]: 'Assignment',
  [ClauseCategory.CONFIDENTIALITY]: 'Confidentiality',
  [ClauseCategory.DELIVERY]: 'Delivery',
  [ClauseCategory.DISPUTE_RESOLUTION]: 'Dispute Resolution',
  [ClauseCategory.FORCE_MAJEURE]: 'Force Majeure',
  [ClauseCategory.GOVERNING_LAW]: 'Governing Law',
  [ClauseCategory.INTELLECTUAL_PROPERTY]: 'Intellectual Property',
  [ClauseCategory.JURISDICTION]: 'Jurisdiction',
  [ClauseCategory.LIABILITY]: 'Liability',
  [ClauseCategory.NOTICES]: 'Notices',
  [ClauseCategory.PAYMENT]: 'Payment',
  [ClauseCategory.SEVERABILITY]: 'Severability',
  [ClauseCategory.SURVIVAL]: 'Survival',
  [ClauseCategory.TERMINATION]: 'Termination',
  [ClauseCategory.OTHER]: 'Other',
};

/**
 * ContractTypeTemplateKey → the label for a code-defined default contract type.
 *
 * Organisation types carry their own `name`; only the Gaia clause library still
 * stores template keys, so this map is what labels those.
 */
export const contractTypeTemplateKeyMap: Record<ContractTypeTemplateKey, string> = {
  [ContractTypeTemplateKey.MSA]: 'MSA',
  [ContractTypeTemplateKey.NDA]: 'NDA',
  [ContractTypeTemplateKey.MUTUAL_NDA]: 'Mutual NDA',
  [ContractTypeTemplateKey.UNILATERAL_NDA]: 'Unilateral NDA',
  [ContractTypeTemplateKey.EMPLOYMENT]: 'Employment',
  [ContractTypeTemplateKey.OFFER_LETTER]: 'Offer Letter',
  [ContractTypeTemplateKey.TERMINATION_AGREEMENT]: 'Termination Agreement',
  [ContractTypeTemplateKey.CONTRACTOR]: 'Contractor',
  [ContractTypeTemplateKey.LEASE]: 'Lease',
  [ContractTypeTemplateKey.PURCHASE]: 'Purchase',
  [ContractTypeTemplateKey.TERMS_AND_CONDITIONS]: 'Terms and Conditions',
  [ContractTypeTemplateKey.SERVICE]: 'Service',
  [ContractTypeTemplateKey.OUTSOURCING_AND_AGENCY_AGREEMENT]: 'Outsourcing and Agency Agreement',
  [ContractTypeTemplateKey.MARKETING_AND_SPONSORSHIP_AGREEMENT]:
    'Marketing and Sponsorship Agreement',
  [ContractTypeTemplateKey.DIGITAL_SERVICES_AGREEMENT]: 'Digital Services Agreement',
  [ContractTypeTemplateKey.E_COMMERCE_AGREEMENT]: 'E-Commerce Agreement',
  [ContractTypeTemplateKey.LICENSING]: 'Licensing',
  [ContractTypeTemplateKey.SOFTWARE_AND_IP_LICENSE]: 'Software and IP License',
  [ContractTypeTemplateKey.DISTRIBUTION]: 'Distribution',
  [ContractTypeTemplateKey.DPA]: 'DPA',
  [ContractTypeTemplateKey.DATA_SECURITY]: 'Data Security',
  [ContractTypeTemplateKey.POWER_OF_ATTORNEY]: 'Power of Attorney',
  [ContractTypeTemplateKey.JOINT_VENTURE]: 'Joint Venture',
  [ContractTypeTemplateKey.PRIVATE_EQUITY]: 'Private Equity',
  [ContractTypeTemplateKey.INCORPORATION_DOCUMENT]: 'Incorporation Document',
  [ContractTypeTemplateKey.BOARD_RESOLUTION]: 'Board Resolution',
  [ContractTypeTemplateKey.PARTNERSHIP]: 'Partnership',
  [ContractTypeTemplateKey.ACQUISITION]: 'Acquisition',
  [ContractTypeTemplateKey.MERGER]: 'Merger',
  [ContractTypeTemplateKey.OTHER]: 'Other',
};

/** The label for the `''` contract-type filter value — contracts without a type. */
export const NO_CONTRACT_TYPE_LABEL = 'No type';

/**
 * Display names for a clause's or playbook's contract types: organisation refs
 * carry their name, Gaia clauses carry template keys.
 */
export function contractTypeNames(
  types: readonly (ContractTypeRef | ContractTypeTemplateKey)[] | null | undefined
): string[] {
  return (types ?? []).map(type =>
    typeof type === 'string' ? contractTypeTemplateKeyMap[type] : type.name
  );
}

/** PlaybookActionType → the label shown on an action row. */
export const playbookActionTypeMap: Record<PlaybookActionType, string> = {
  [PlaybookActionType.SUGGESTION]: 'Suggest wording',
  [PlaybookActionType.NOTIFICATION]: 'Notify',
  [PlaybookActionType.EVALUATION]: 'Run evaluation',
  [PlaybookActionType.SIGNATURE_PREPARATION]: 'Prepare signature',
  [PlaybookActionType.SLACK_NOTIFICATION]: 'Notify in Slack',
  [PlaybookActionType.JIRA_NOTIFICATION]: 'Notify in Jira',
};
